import React, { useState } from "react";
import "./ProductTips.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";

const tips = [
    () => (
        <>
            <h2 className="title">
                Bring your survey ideas to life—just by describing them
            </h2>
            <p>The Web View Step Copilot helps you design rich, interactive survey experiences with no coding 
                required. Describe the type of step or interaction you want, and the Copilot will generate the 
                underlying HTML, CSS, and JavaScript for you.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/web-view-step-copilot.png" alt="Web view step copilot"></img>
            <p>Try asking Copilot:</p>
            <ul>
                <li>"Make a mood tracker with clickable emojis"</li>
                <li>"Display a congratulatory message if the participant has 10,000 steps from their Fitbit today"</li>
                <li>"Generate a 30-second reaction time test with a submit button"</li>
            </ul>
            <p>Tip: Click the info tooltip in the Web View Step Editor to explore more example prompts.</p>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Confidently promote tested updates to your live project
            </h2>
            <p>Using Project Compare, copy over updates you made and tested in a Dry Run project to a live project.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/project-compare.png" alt="Project compare"></img>
            <p>Use it to:</p>
            <ul>
                <li>Spot configuration differences between projects at a glance</li>
                <li>Copy surveys, segments, schedules, and more from Dry Run to Production</li>
                <li>Keep your live project aligned with tested changes</li>
                <li>Confidently promote updates without manual work</li>
            </ul>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Pin segments to keep track of critical participant groups
            </h2>
            <p>Once a segment is featured, it's visible to all project users, keeping your team aligned & focused. Just 
                click the gear icon above the Participants list to select the segments you want to feature.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/featured-segments.png" alt="Featured segments"></img>
            <p>Study teams often use Featured Segments to:</p>
            <ul>
                <li>Quickly view participants who haven't completed enrollment</li>
                <li>Monitor key milestones and study progress of participants</li>
                <li>Focus on high-priority groups needing outreach or intervention</li>
            </ul>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Create personalized participant journeys
            </h2>
            <p>App Designer enables you to customize the participant journey, not only for your project, but for 
                each individual participant.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/app-designer.png" alt="App designer"></img>
            <p>Using conditional sections and other basic App Designer components, you can:</p>
            <ul>
                <li>Direct participants to their next best action—whether that's completing a survey, connecting 
                    to an EHR or wearable device, or placing an order for a study device or drug.</li>
                <li>Surface personalized, engaging content, specific to a participant—if your participant 
                    indicates they have not been sleeping well, they could be shown an article on developing 
                    better sleep habits.</li>
            </ul>
            <p>Tip: Thoroughly test your conditional logic to ensure a smooth participant experience.</p>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Automatically send participants their signed consent form
            </h2>
            <p>When setting up your consent or consent form step, choose an email notification and the participant will receive their signed consent form as a PDF attachment.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/send-signed-consent-form.png" alt="Send signed consent form"></img>
            <p>Here's how:</p>
            <ul>
                <li>Create an email notification (for example, using the Informational V2 email template)</li>
                <li>Link the notification to your consent or consent form step</li>
                <li>Publish both the updated survey and the new notification</li>
            </ul>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Enhance Your Surveys with Images and Rich Formatting
            </h2>
            <p>Use the rich text editor and GitHub Flavored Markdown to easily add custom formatting, visuals, 
                and structured content to your survey steps.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/enhance-surveys.png" alt="Enhance surveys"></img>
            <p>Use it to:</p>
            <ul>
                <li>Insert images directly into your survey content where relevant</li>
                <li>Add tables, headings, code blocks, multi-level lists, and other formatting elements</li>
                <li>Copy and paste pre-formatted content while preserving its layout and style</li>
            </ul>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Monitor participant compliance using Segments
            </h2>
            <p>Monitor who is on track, who needs follow-up, and when to intervene—then trigger automated actions 
                when they meet specific criteria.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/monitoring-compliance-using-segments.png" alt="Monitoring compliance using segments"></img>
            <p>Use it to:</p>
            <ul>
                <li>Flag participants who haven't completed recent surveys or tasks</li>
                <li>Monitor wearable device sync and reconnection needs</li>
                <li>Track study milestone completion (Baseline, clinic visits, follow-ups, etc)</li>
            </ul>
            <p>Once you set up a segment, automate your outreach using an On Entering a Segment schedule.</p>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Capture participant touchpoints and status updates using Coordinator Surveys
            </h2>
            <p>Coordinator Surveys let you log contact notes, visits, participant status, and other additional 
                information directly within each participant's record. Use captured information to power 
                automated workflows or update segments across your project.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/coordinator-surveys.png" alt="Coordinator surveys"></img>
            <p>Build surveys that coordinators can complete to do the following:</p>
            <ul>
                <li>Log calls, notes, clinic visits, and surgery dates</li>
                <li>Contact participants and logging information</li>
                <li>Document withdrawal reason</li>
                <li>Update participant status</li>
            </ul>
        </>
    ),

];

const ProductTips: React.FC = () => {
    const [currentTipIndex, setCurrentTipIndex] = useState(() => Math.floor(Math.random() * tips.length));

    const handleNext = () => {
        setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
    };

    const handlePrev = () => {
        setCurrentTipIndex((prevIndex) => (prevIndex - 1 + tips.length) % tips.length);
    };

    return (
        <>
            <div className="product-tips">
            <header>
                <button onClick={handlePrev} title="previous">
                    <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <h3>Discover what's possible</h3>
                <button onClick={handleNext} title="next">
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </header>
            <div className="product-tip">{tips[currentTipIndex]()}</div>
        </div>
        </>
    );
};

export default ProductTips;
